// ===================================
// ADMIN PROGRAMS PAGE - FIXED
// src/pages/AdminPrograms.js
// FIX: Use api service instead of raw fetch
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './AdminPrograms.css';

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    program_type: 'course',
    slug: '',
    name: '',
    description: '',
    duration: '',
    skills: '',
    career_paths: '',
    prerequisites: '',
    price: '',
    display_order: 0
  });

  // FIXED: Use api service instead of raw fetch
  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/programs/admin/all');
      setPrograms(response.data.programs || []);
    } catch (err) {
      console.error('Failed to fetch programs:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    let filtered = programs;
    
    // Filter by type
    if (filter !== 'all') {
      filtered = filtered.filter(p => p.program_type === filter);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.career_paths?.some(cp => cp.toLowerCase().includes(query))
      );
    }
    
    setFilteredPrograms(filtered);
  }, [programs, filter, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Process arrays
      const payload = {
        ...formData,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : [],
        career_paths: formData.career_paths ? formData.career_paths.split(',').map(s => s.trim()) : [],
        prerequisites: formData.prerequisites ? formData.prerequisites.split(',').map(s => s.trim()) : [],
        price: formData.price ? parseFloat(formData.price) : null,
        display_order: parseInt(formData.display_order) || 0
      };

      if (editingProgram) {
        await api.put(`/programs/admin/${editingProgram.id}`, payload);
      } else {
        await api.post('/programs/admin', payload);
      }
      
      setShowModal(false);
      setEditingProgram(null);
      resetForm();
      fetchPrograms();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save program');
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to deactivate this program?')) return;
    
    try {
      await api.delete(`/programs/admin/${programId}`);
      fetchPrograms();
    } catch (err) {
      alert('Failed to delete program');
    }
  };

  const openEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      program_type: program.program_type,
      slug: program.slug,
      name: program.name,
      description: program.description || '',
      duration: program.duration || '',
      skills: Array.isArray(program.skills) ? program.skills.join(', ') : '',
      career_paths: Array.isArray(program.career_paths) ? program.career_paths.join(', ') : '',
      prerequisites: Array.isArray(program.prerequisites) ? program.prerequisites.join(', ') : '',
      price: program.price || '',
      display_order: program.display_order || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      program_type: 'course',
      slug: '',
      name: '',
      description: '',
      duration: '',
      skills: '',
      career_paths: '',
      prerequisites: '',
      price: '',
      display_order: 0
    });
  };

  const programTypes = [
    { value: 'all', label: 'All Programs', icon: '📚' },
    { value: 'career_program', label: 'Career Programs', icon: '🚀' },
    { value: 'course', label: 'Courses', icon: '📖' },
    { value: 'certification', label: 'Certifications', icon: '🏆' },
    { value: 'bootcamp', label: 'Bootcamps', icon: '💻' }
  ];

  const getTypeIcon = (type) => {
    const found = programTypes.find(t => t.value === type);
    return found ? found.icon : '📋';
  };

  if (loading) {
    return (
      <div className="admin-programs-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading programs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-programs-page">
      <div className="page-header">
        <div className="header-content">
          <h1>📚 Academy Programs</h1>
          <p>Manage courses, certifications, and training programs</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setEditingProgram(null);
            setShowModal(true);
          }}
        >
          + Add Program
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={fetchPrograms} className="btn btn-sm btn-secondary" style={{ marginLeft: '1rem' }}>
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-tabs">
          {programTypes.map(type => (
            <button
              key={type.value}
              className={`filter-tab ${filter === type.value ? 'active' : ''}`}
              onClick={() => setFilter(type.value)}
            >
              {type.icon} {type.label}
              {type.value === 'all' && <span className="count">{programs.length}</span>}
              {type.value !== 'all' && (
                <span className="count">
                  {programs.filter(p => p.program_type === type.value).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search programs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>×</button>
          )}
        </div>
      </div>

      {/* Programs Grid */}
      <div className="programs-grid">
        {filteredPrograms.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No programs found</h3>
            <p>
              {searchQuery 
                ? 'Try adjusting your search' 
                : filter !== 'all' 
                  ? `No ${filter.replace('_', ' ')}s yet` 
                  : 'Create your first program to get started'}
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setEditingProgram(null);
                setShowModal(true);
              }}
            >
              + Create Program
            </button>
          </div>
        ) : (
          filteredPrograms.map(program => (
            <div key={program.id} className={`program-card ${!program.is_active ? 'inactive' : ''}`}>
              <div className="program-header">
                <span className="program-type-icon">{getTypeIcon(program.program_type)}</span>
                <span className="program-type-badge">{program.program_type.replace('_', ' ')}</span>
                {!program.is_active && <span className="inactive-badge">Inactive</span>}
              </div>
              
              <h3 className="program-name">{program.name}</h3>
              <p className="program-description">{program.description}</p>
              
              <div className="program-meta">
                {program.duration && (
                  <span className="meta-item">
                    ⏱️ {program.duration}
                  </span>
                )}
                {program.price && (
                  <span className="meta-item">
                    💰 ${program.price}
                  </span>
                )}
              </div>

              {program.skills && program.skills.length > 0 && (
                <div className="program-skills">
                  {program.skills.slice(0, 3).map((skill, idx) => (
                    <span key={idx} className="skill-tag">{skill}</span>
                  ))}
                  {program.skills.length > 3 && (
                    <span className="skill-tag more">+{program.skills.length - 3}</span>
                  )}
                </div>
              )}

              {program.career_paths && program.career_paths.length > 0 && (
                <div className="program-careers">
                  <span className="careers-label">Career Paths:</span>
                  {program.career_paths.slice(0, 2).map((path, idx) => (
                    <span key={idx} className="career-tag">{path}</span>
                  ))}
                </div>
              )}

              <div className="program-actions">
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => openEdit(program)}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(program.id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Program Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProgram ? 'Edit Program' : 'New Program'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.program_type}
                      onChange={e => setFormData({ ...formData, program_type: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="course">Course</option>
                      <option value="career_program">Career Program</option>
                      <option value="certification">Certification</option>
                      <option value="bootcamp">Bootcamp</option>
                    </select>
                  </div>
                  <div className="form-group flex-grow">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="form-input"
                      required
                      placeholder="e.g., Interview Mastery Course"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={e => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="form-input"
                    required
                    placeholder="e.g., interview-mastery"
                  />
                </div>
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="form-input"
                    rows={3}
                    placeholder="What students will learn..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Duration</label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={e => setFormData({ ...formData, duration: e.target.value })}
                      className="form-input"
                      placeholder="e.g., 4 weeks"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price ($)</label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={e => setFormData({ ...formData, price: e.target.value })}
                      className="form-input"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={e => setFormData({ ...formData, display_order: e.target.value })}
                      className="form-input"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Skills (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.skills}
                    onChange={e => setFormData({ ...formData, skills: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Communication, Problem Solving, Leadership"
                  />
                </div>

                <div className="form-group">
                  <label>Career Paths (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.career_paths}
                    onChange={e => setFormData({ ...formData, career_paths: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Software Engineer, Product Manager"
                  />
                </div>

                <div className="form-group">
                  <label>Prerequisites (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.prerequisites}
                    onChange={e => setFormData({ ...formData, prerequisites: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Basic computer skills, Resume ready"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProgram ? 'Update' : 'Create'} Program
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrograms;